import { useState } from 'react';
import { Modal, Button, Input, Label, Spinner } from '@theraply/ui';
import { AvailabilitySlot } from '@theraply/api-client';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SESSION_TYPES = [
  { value: 'video', label: 'Video' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'both', label: 'Both' },
];

interface SlotEditorProps {
  slot?: AvailabilitySlot | null;
  isOpen: boolean;
  isLoading: boolean;
  onSave: (data: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    sessionType: string;
    bufferMinutes: number;
  }) => void;
  onDelete?: (id: number) => void;
  onClose: () => void;
}

export default function SlotEditor({
  slot,
  isOpen,
  isLoading,
  onSave,
  onDelete,
  onClose,
}: SlotEditorProps) {
  const [dayOfWeek, setDayOfWeek] = useState(slot?.dayOfWeek ?? 0);
  const [startTime, setStartTime] = useState(slot?.startTime ?? '09:00');
  const [endTime, setEndTime] = useState(slot?.endTime ?? '17:00');
  const [sessionType, setSessionType] = useState(slot?.sessionType ?? 'video');
  const [bufferMinutes, setBufferMinutes] = useState(slot?.bufferMinutes ?? 15);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);

    if (!startTime || !endTime) {
      setError('Start time and end time are required');
      return;
    }

    if (startTime >= endTime) {
      setError('Start time must be before end time');
      return;
    }

    if (bufferMinutes < 0) {
      setError('Buffer minutes cannot be negative');
      return;
    }

    onSave({
      dayOfWeek,
      startTime,
      endTime,
      sessionType,
      bufferMinutes,
    });
  };

  const handleDelete = () => {
    if (slot && onDelete && window.confirm('Are you sure you want to delete this slot?')) {
      onDelete(slot.id);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {slot ? 'Edit Availability Slot' : 'Create Availability Slot'}
        </h2>

        <div className="space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <Label htmlFor="day-of-week">Day of Week</Label>
            <select
              id="day-of-week"
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(parseInt(e.target.value))}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {DAYS_OF_WEEK.map((day, index) => (
                <option key={index} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-time">Start Time</Label>
              <Input
                id="start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end-time">End Time</Label>
              <Input
                id="end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="session-type">Session Type</Label>
            <select
              id="session-type"
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {SESSION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="buffer-minutes">Buffer Between Appointments (minutes)</Label>
            <Input
              id="buffer-minutes"
              type="number"
              min="0"
              value={bufferMinutes}
              onChange={(e) => setBufferMinutes(parseInt(e.target.value))}
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          {slot && onDelete && (
            <Button
              onClick={handleDelete}
              disabled={isLoading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? <Spinner /> : 'Delete'}
            </Button>
          )}
          <Button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white"
          >
            {isLoading ? <Spinner /> : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
